import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, Check, FlaskConical, RotateCcw, Save } from 'lucide-react';
import { useAuth } from '@/contexts/auth-hook';
import {
  AvaliacaoSquad,
  PERIODOS,
  PERIODO_ATUAL,
  PILARES,
  Periodo,
  PilarId,
  SQUADS_DEMO,
  calcularIndice,
  calcularScores,
  descartarAvaliacao,
  faixaDoIndice,
  foiPreenchidaPeloLider,
  formatarMoeda,
  gerarAvaliacao,
  obterAvaliacao,
  obterPesos,
  salvarAvaliacao,
} from './squad-metrics';

type CampoNumerico = Exclude<keyof AvaliacaoSquad, 'squadId' | 'periodo' | 'gargalos' | 'acaoProposta'>;

interface Campo {
  id: CampoNumerico;
  rotulo: string;
  ajuda: string;
  min: number;
  max: number;
  passo: number;
  sufixo?: string;
  /** Campos em dinheiro entram por caixa de texto, não por slider. */
  moeda?: boolean;
}

const CAMPOS: Record<PilarId, Campo[]> = {
  capacidade: [
    { id: 'headcount', rotulo: 'Pessoas no squad', ajuda: 'Quantas pessoas o squad tem hoje', min: 1, max: 40, passo: 1 },
    { id: 'alocacaoMedia', rotulo: 'Alocação média', ajuda: 'Acima de 100% significa time absorvendo mais do que cabe', min: 30, max: 145, passo: 1, sufixo: '%' },
    { id: 'ociosidade', rotulo: 'Ociosidade', ajuda: 'Percentual do tempo sem frente definida', min: 0, max: 60, passo: 1, sufixo: '%' },
    { id: 'horasExtrasMes', rotulo: 'Horas extras por mês', ajuda: 'Média por pessoa', min: 0, max: 60, passo: 1, sufixo: 'h' },
  ],
  entrega: [
    { id: 'entregasConcluidas', rotulo: 'Entregas concluídas', ajuda: 'Quantas entregas fecharam no período — é a vazão real do squad', min: 1, max: 120, passo: 1 },
    { id: 'entregasNoPrazo', rotulo: 'Entregas no prazo', ajuda: 'Percentual que fechou dentro da data combinada', min: 0, max: 100, passo: 1, sufixo: '%' },
    { id: 'retrabalho', rotulo: 'Retrabalho', ajuda: 'Percentual das entregas que voltou para correção', min: 0, max: 60, passo: 1, sufixo: '%' },
    { id: 'backlogItens', rotulo: 'Backlog', ajuda: 'Itens em aberto no fim do período', min: 0, max: 200, passo: 1 },
  ],
  custo: [
    { id: 'budgetPlanejado', rotulo: 'Budget planejado', ajuda: 'Quanto estava previsto para o período', min: 0, max: 5_000_000, passo: 1000, moeda: true },
    { id: 'budgetConsumido', rotulo: 'Budget consumido', ajuda: 'Quanto de fato foi gasto', min: 0, max: 5_000_000, passo: 1000, moeda: true },
  ],
  pessoas: [
    { id: 'clima', rotulo: 'Clima do time', ajuda: '0 = time desmoralizado, 10 = time engajado', min: 0, max: 10, passo: 0.1 },
    { id: 'sobrecarga', rotulo: 'Sobrecarga percebida', ajuda: '0 = tranquilo, 10 = no limite', min: 0, max: 10, passo: 0.1 },
    { id: 'riscoSaida', rotulo: 'Risco de saída', ajuda: 'Chance de perder alguém importante nos próximos meses', min: 0, max: 10, passo: 0.1 },
    { id: 'pessoasChave', rotulo: 'Pessoas que seguram conhecimento crítico', ajuda: 'Quanto menor, mais o squad quebra se alguém sair', min: 1, max: 12, passo: 1 },
  ],
};

export function SquadAvaliacaoPage() {
  const { user, isAdmin } = useAuth();

  // Sem banco, o vínculo com o squad real do usuário é só uma sugestão inicial.
  const squadInicial = useMemo(() => {
    const porNome = SQUADS_DEMO.find((s) => s.nome === user?.squadNome);
    return porNome?.id ?? SQUADS_DEMO[0].id;
  }, [user?.squadNome]);

  const [squadId, setSquadId] = useState(squadInicial);
  const [periodo, setPeriodo] = useState<Periodo>(PERIODO_ATUAL);
  const [form, setForm] = useState<AvaliacaoSquad>(() => obterAvaliacao(squadInicial, PERIODO_ATUAL));
  const [salvo, setSalvo] = useState(false);

  useEffect(() => {
    setForm(obterAvaliacao(squadId, periodo));
    setSalvo(false);
  }, [squadId, periodo]);

  const pesos = obterPesos();
  const scores = calcularScores(form);
  const indice = calcularIndice(scores, pesos);
  const faixa = faixaDoIndice(indice);
  const jaPreenchida = foiPreenchidaPeloLider(squadId, periodo);
  const squad = SQUADS_DEMO.find((s) => s.id === squadId);

  const definir = (campo: CampoNumerico, valor: number) => {
    setForm((f) => ({ ...f, [campo]: valor }));
    setSalvo(false);
  };

  const handleSalvar = () => {
    salvarAvaliacao({ ...form, squadId, periodo });
    setSalvo(true);
  };

  const handleRestaurar = () => {
    descartarAvaliacao(squadId, periodo);
    setForm(gerarAvaliacao(squadId, periodo));
    setSalvo(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-schmersal-gray-dark dark:text-white">Avaliação do Squad</h1>
          <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light mt-1">
            Preenchido pelo líder a cada período. É isto que alimenta o painel de overview.
          </p>
        </div>
        <Link
          to="/squads/overview"
          className="inline-flex items-center px-4 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg text-schmersal-gray-dark dark:text-white hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark transition-colors shrink-0"
        >
          <BarChart3 className="h-4 w-4 mr-2" />
          Ver overview
        </Link>
      </div>

      <div className="flex items-start gap-3 rounded-lg border border-schmersal-yellow bg-schmersal-yellow/10 p-4">
        <FlaskConical className="h-5 w-5 text-schmersal-yellow-dark shrink-0 mt-0.5" aria-hidden />
        <div className="text-sm text-schmersal-gray-dark dark:text-schmersal-gray-light">
          <strong className="text-schmersal-gray-dark dark:text-white">Nada disso vai para o banco ainda.</strong> Os
          campos abrem com um cenário de demonstração e o que você salvar fica só neste navegador — dá para mexer à
          vontade e usar <em>Restaurar</em> para voltar ao original. Quando os pilares estiverem fechados, isto vira
          tabela e API de verdade.
        </div>
      </div>

      {/* Escopo do preenchimento */}
      <div className="flex flex-wrap items-end gap-4 bg-white dark:bg-schmersal-gray-darker rounded-lg shadow p-4 border border-rg-line dark:border-schmersal-gray-dark">
        <div>
          <label htmlFor="squad" className="block text-xs font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light mb-1">
            Squad
          </label>
          <select
            id="squad"
            value={squadId}
            onChange={(e) => setSquadId(e.target.value)}
            className="px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white text-sm"
          >
            {SQUADS_DEMO.map((s) => (
              <option key={s.id} value={s.id}>
                {s.nome}
                {s.real ? '' : ' (hipótese)'}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="periodo" className="block text-xs font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light mb-1">
            Período
          </label>
          <select
            id="periodo"
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value as Periodo)}
            className="px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white text-sm"
          >
            {PERIODOS.map((p) => (
              <option key={p} value={p}>
                {p}
                {p === PERIODO_ATUAL ? ' (atual)' : ''}
              </option>
            ))}
          </select>
        </div>
        <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">
          {squad?.regiao}
          {jaPreenchida ? ' · já preenchido' : ' · usando o cenário de demonstração'}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Formulário */}
        <div className="lg:col-span-2 space-y-6">
          {PILARES.map((pilar) => (
            <section
              key={pilar.id}
              className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow-sm p-5 border border-rg-line dark:border-schmersal-gray-dark"
            >
              <div className="flex items-baseline justify-between gap-4 mb-4">
                <div>
                  <h2 className="font-semibold text-schmersal-gray-dark dark:text-white">{pilar.nome}</h2>
                  <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">{pilar.pergunta}</p>
                </div>
                <span className="text-sm tabular-nums text-schmersal-gray-medium dark:text-schmersal-gray-light shrink-0">
                  {scores[pilar.id].toFixed(0)}/100
                </span>
              </div>

              <div className="space-y-5">
                {CAMPOS[pilar.id].map((campo) => (
                  <div key={campo.id}>
                    <div className="flex items-baseline justify-between gap-4 mb-1">
                      <label
                        htmlFor={campo.id}
                        className="text-sm font-medium text-schmersal-gray-dark dark:text-white"
                      >
                        {campo.rotulo}
                      </label>
                      <span className="text-sm tabular-nums font-semibold text-schmersal-gray-dark dark:text-white shrink-0">
                        {campo.moeda
                          ? formatarMoeda(form[campo.id] as number)
                          : `${form[campo.id]}${campo.sufixo ?? ''}`}
                      </span>
                    </div>

                    {campo.moeda ? (
                      <input
                        id={campo.id}
                        type="number"
                        min={campo.min}
                        max={campo.max}
                        step={campo.passo}
                        value={form[campo.id] as number}
                        onChange={(e) => definir(campo.id, Number(e.target.value))}
                        className="w-full px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white"
                      />
                    ) : (
                      <input
                        id={campo.id}
                        type="range"
                        min={campo.min}
                        max={campo.max}
                        step={campo.passo}
                        value={form[campo.id] as number}
                        onChange={(e) => definir(campo.id, Number(e.target.value))}
                        className="w-full accent-schmersal-blue dark:accent-schmersal-turquoise"
                      />
                    )}
                    <p className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light mt-1">
                      {campo.ajuda}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          ))}

          <section className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow-sm p-5 border border-rg-line dark:border-schmersal-gray-dark">
            <h2 className="font-semibold text-schmersal-gray-dark dark:text-white">Leitura do líder</h2>
            <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light mb-4">
              O número mostra onde dói. Esta parte é a que explica por quê — e costuma ser a mais lida.
            </p>
            <div className="space-y-4">
              <div>
                <label htmlFor="gargalos" className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">
                  Principal gargalo do período
                </label>
                <textarea
                  id="gargalos"
                  rows={3}
                  value={form.gargalos}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, gargalos: e.target.value }));
                    setSalvo(false);
                  }}
                  className="w-full px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="acao" className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">
                  O que você mudaria na estrutura
                </label>
                <textarea
                  id="acao"
                  rows={3}
                  value={form.acaoProposta}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, acaoProposta: e.target.value }));
                    setSalvo(false);
                  }}
                  className="w-full px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white"
                />
              </div>
            </div>
          </section>
        </div>

        {/* Prévia: o líder vê o efeito do que responde */}
        <aside className="lg:sticky lg:top-6 h-fit space-y-4">
          <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow-sm p-5 border border-rg-line dark:border-schmersal-gray-dark">
            <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">
              Índice de Saúde · prévia
            </p>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-5xl font-semibold text-schmersal-gray-dark dark:text-white leading-none">
                {indice.toFixed(0)}
              </span>
              <span className="text-schmersal-gray-medium dark:text-schmersal-gray-light">/ 100</span>
            </div>
            <p className="inline-flex items-center gap-1.5 text-sm mt-2 text-schmersal-gray-dark dark:text-white">
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: faixa.cor }} aria-hidden />
              {faixa.rotulo}
            </p>

            <div className="mt-5 space-y-3">
              {PILARES.map((pilar) => (
                <div key={pilar.id}>
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="text-schmersal-gray-dark dark:text-white">{pilar.nome}</span>
                    <span className="tabular-nums text-schmersal-gray-medium dark:text-schmersal-gray-light">
                      {scores[pilar.id].toFixed(0)} · peso {pesos[pilar.id]}%
                    </span>
                  </div>
                  {/* Medidor: trilho é um passo mais claro da própria cor da barra. */}
                  <div className="h-1.5 rounded-full bg-schmersal-gray-light dark:bg-schmersal-gray-dark mt-1 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-schmersal-blue dark:bg-schmersal-turquoise"
                      style={{ width: `${scores[pilar.id]}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-2 mt-6">
              <button
                type="button"
                onClick={handleSalvar}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-schmersal-blue text-white rounded-lg hover:bg-schmersal-blue-dark transition-colors"
              >
                {salvo ? <Check className="h-4 w-4" /> : <Save className="h-4 w-4" />}
                {salvo ? 'Salvo neste navegador' : 'Salvar avaliação'}
              </button>
              <button
                type="button"
                onClick={handleRestaurar}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg text-schmersal-gray-dark dark:text-white hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark transition-colors"
              >
                <RotateCcw className="h-4 w-4" />
                Restaurar demonstração
              </button>
            </div>
          </div>

          {!isAdmin && (
            <p className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light px-1">
              Com o banco ligado, o seletor de squad some: cada líder preenche apenas o próprio squad, seguindo a mesma
              regra de escopo do resto do sistema.
            </p>
          )}
        </aside>
      </div>
    </div>
  );
}
